import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Card, Badge } from './V2Components';
import { getUtilityMeta, getUtilityDisplayName } from '../../utils/utilityIcons';
import { isSafeHttpUrl } from '../../utils/validation';

const formatCurrency = (value) => {
  if (value == null || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
};

const UtilityCard = ({ utility, onClick }) => {
  const meta = getUtilityMeta(utility.type);
  const Icon = meta.icon;
  const name = getUtilityDisplayName(utility);
  const avg = utility.monthly_cost_average;
  const billCount = utility.bill_count || 0;
  // Defense-in-depth: the backend already rejects non-http(s) URLs at
  // create/update (schemas/utility.py:url_scheme), but a legacy or out-of-band
  // row with `javascript:` would otherwise execute on click. Gate the link
  // render on a safe URL so the anchor simply doesn't appear for bad data.
  const accountHref = isSafeHttpUrl(utility.account_url);

  return (
    <Card
      onClick={onClick}
      className="p-5 cursor-pointer hover:shadow-md transition-shadow"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-xl bg-[var(--bg-hover)] ${meta.color}`}>
          <Icon size={24} />
        </div>
        <Badge status={billCount > 0 ? `${billCount} ${billCount === 1 ? 'bill' : 'bills'}` : 'No bills'} />
      </div>
      <div className="font-bold text-[var(--text-main)] truncate" title={name}>
        {name}
      </div>
      <div className="text-xs text-[var(--text-secondary)] capitalize mb-3">
        {meta.label}
      </div>
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs text-[var(--text-muted)]">Avg / month</div>
          <div className="text-xl font-bold text-[var(--primary)]">
            {formatCurrency(avg)}
          </div>
        </div>
        {accountHref && (
          <a
            href={accountHref}
            target="_blank"
            rel="noopener noreferrer"
            // Stop the click/keydown from bubbling to the card's navigation
            // handler — this links out to the provider, the card goes to detail.
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-xs text-[var(--text-secondary)] hover:text-[var(--primary)]"
            aria-label="Open provider account"
            title="Open provider account"
          >
            <ExternalLink size={14} />
            <span>Account</span>
          </a>
        )}
      </div>
    </Card>
  );
};

export default UtilityCard;
